import { useEffect, useMemo, useState } from 'react'
import { Bluetooth, BluetoothConnected, BluetoothOff, Scan, Settings2 } from 'lucide-react'
import { Card } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/Modal'
import { safeStorageGet, safeStorageSet } from '../../../utils/storage'
import { useToast } from '../../../components/Toast'

const STORE_KEY = 'anipin.preview.devices.v1'

function loadSavedDevices() {
  try {
    const raw = safeStorageGet(STORE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveDevices(devices) {
  safeStorageSet(STORE_KEY, JSON.stringify(Array.isArray(devices) ? devices : []))
}

function normalizeSavedDevice(d) {
  const obj = d && typeof d === 'object' ? d : {}
  const id = String(obj.id || '').trim()
  const name = String(obj.name || '').trim()
  if (!id) return null
  return { id, name: name || id, lastSeenAt: Number(obj.lastSeenAt) || Date.now() }
}

export default function AppPreviewDevices() {
  const { toast } = useToast()
  const [scanModalOpen, setScanModalOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [connectedId, setConnectedId] = useState('')

  const [saved, setSaved] = useState(() => loadSavedDevices().map(normalizeSavedDevice).filter(Boolean))

  useEffect(() => {
    saveDevices(saved)
  }, [saved])

  const bluetoothSupported = useMemo(() => {
    try {
      return Boolean(navigator.bluetooth)
    } catch {
      return false
    }
  }, [])

  const startScan = async () => {
    if (!bluetoothSupported) {
      toast({ kind: 'warning', title: '当前浏览器不支持蓝牙', message: '请用 Chrome / Edge（Android）测试 Web Bluetooth。' })
      return
    }

    setBusy(true)
    try {
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [],
      })

      const id = String(device?.id || '').trim() || `dev_${Date.now()}`
      const name = String(device?.name || '').trim() || 'Unknown device'

      setSaved((prev) => {
        const merged = [{ id, name, lastSeenAt: Date.now() }, ...prev].filter(Boolean)
        const unique = []
        const seen = new Set()
        for (const item of merged) {
          if (!item?.id || seen.has(item.id)) continue
          seen.add(item.id)
          unique.push(item)
        }
        return unique.slice(0, 30)
      })

      toast({ kind: 'success', title: '已选择设备', message: name })
    } catch (e) {
      const msg = String(e?.message || e || '').trim()
      if (msg && !msg.toLowerCase().includes('cancel')) {
        toast({ kind: 'warning', title: '扫描已取消/失败', message: msg })
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Bluetooth className="w-5 h-5 text-brand-textDark" strokeWidth={2.6} />
              <div className="text-base font-black tracking-tight">Nearby</div>
            </div>
            <div className="mt-1 text-sm font-bold text-brand-text/70">
              {bluetoothSupported ? '扫描附近的设备并连接（一次只连 1 台）。' : '该浏览器不支持 Web Bluetooth。'}
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-3">
          <Button
            variant="secondary"
            size="lg"
            className="w-full justify-center"
            disabled={busy}
            onClick={() => setScanModalOpen(true)}
          >
            <Scan className="w-5 h-5" />
            Scan
          </Button>
          <Button variant="neutral" size="md" className="w-full justify-center" onClick={() => setHelpOpen(true)}>
            <Settings2 className="w-4 h-4" />
            BT settings
          </Button>
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="text-base font-black tracking-tight">Other devices</div>
          <div className="text-xs font-bold text-brand-text/70">{saved.length ? `${saved.length} saved` : '0 saved'}</div>
        </div>

        {saved.length ? (
          <div className="mt-4 grid gap-3">
            {saved.map((d) => {
              const isConnected = d.id === connectedId
              return (
                <div
                  key={d.id}
                  className="rounded-3xl border-2 border-brand-textDark/15 dark:border-slate-700 bg-white dark:bg-slate-950 px-4 py-4 flex items-center justify-between gap-3 overflow-hidden"
                >
                  <div className="min-w-0">
                    <div className="font-extrabold truncate">{d.name}</div>
                    <div className="mt-1 text-xs font-mono text-brand-text/70 truncate">{d.id}</div>
                  </div>
                  <Button
                    variant={isConnected ? 'neutral' : 'secondary'}
                    size="sm"
                    onClick={() => setConnectedId(isConnected ? '' : d.id)}
                    className="shrink-0"
                  >
                    {isConnected ? <BluetoothConnected className="w-4 h-4" /> : <BluetoothOff className="w-4 h-4" />}
                    {isConnected ? 'Disconnect' : 'Connect'}
                  </Button>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="mt-4 rounded-3xl border-2 border-brand-textDark/10 bg-brand-bg px-4 py-4 text-sm font-bold text-brand-text/70">
            No saved devices yet. Tap <span className="font-black">Scan</span> to add one.
          </div>
        )}
      </Card>

      <Modal
        open={scanModalOpen}
        onClose={() => (busy ? null : setScanModalOpen(false))}
        title="Connect via Bluetooth"
        footer={
          <>
            <Button variant="neutral" onClick={() => setScanModalOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={async () => {
                await startScan()
                setScanModalOpen(false)
              }}
              disabled={busy}
              className="min-w-[170px] justify-center"
            >
              <Scan className="w-4 h-4" />
              Allow & scan
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm font-bold text-brand-textDark/80 dark:text-slate-200">
          <div>我们会打开系统弹窗，让你选择要连接的设备。</div>
          <div className="text-brand-text/70">
            提示：Web 蓝牙通常只在 Android Chrome/Edge 可用；iOS Safari 可能不支持。
          </div>
        </div>
      </Modal>

      <Modal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="Bluetooth settings"
        footer={
          <Button variant="secondary" onClick={() => setHelpOpen(false)}>
            OK
          </Button>
        }
      >
        <div className="space-y-3 text-sm font-bold text-brand-textDark/80 dark:text-slate-200">
          <div>1) 打开系统蓝牙</div>
          <div>2) 确保设备在可配对/可发现状态</div>
          <div>3) 回到本页点击 Scan</div>
        </div>
      </Modal>
    </div>
  )
}
